import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveSrc = (u) => (!u ? '' : u.startsWith('http') ? u : `${BACKEND_URL}${u}`);

/**
 * Full-screen image lightbox with keyboard (Esc/←/→) and touch-swipe navigation.
 * Props: { open, photos: string[], initialIndex, title, onClose }
 */
const Lightbox = ({ open, photos = [], initialIndex = 0, title = '', onClose }) => {
  const [idx, setIdx] = useState(initialIndex);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (open) setIdx(initialIndex || 0);
  }, [open, initialIndex]);

  const next = useCallback(() => {
    if (photos.length > 1) setIdx((i) => (i + 1) % photos.length);
  }, [photos.length]);

  const prev = useCallback(() => {
    if (photos.length > 1) setIdx((i) => (i - 1 + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, next, prev, onClose]);

  if (!open || photos.length === 0) return null;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next(); else prev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] backdrop-blur-sm flex flex-col items-center justify-center select-none"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="lightbox-root"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent z-10">
        <div className="text-white/90 font-medium text-sm truncate max-w-[70%]" data-testid="lightbox-title">{title}</div>
        <div className="flex items-center gap-3">
          <span className="text-white/80 text-xs font-bold bg-white/10 px-2 py-1 rounded" data-testid="lightbox-counter">
            {idx + 1} / {photos.length}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
            className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-9 h-9 flex items-center justify-center transition"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Main image */}
      <img
        src={resolveSrc(photos[idx])}
        alt={title ? `${title} — ${idx + 1}` : `Image ${idx + 1}`}
        className="max-w-[92vw] max-h-[78vh] object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />

      {/* Prev / Next */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full w-11 h-11 md:w-14 md:h-14 flex items-center justify-center transition backdrop-blur-sm"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full w-11 h-11 md:w-14 md:h-14 flex items-center justify-center transition backdrop-blur-sm"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <CaretRight size={22} weight="bold" />
          </button>
        </>
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 z-10">
          <div className="flex gap-2 justify-center overflow-x-auto max-w-full">
            {photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition ${i === idx ? 'border-white' : 'border-white/20 opacity-60 hover:opacity-100'}`}
                aria-label={`Go to image ${i + 1}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={resolveSrc(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Lightbox;
